import { useEffect, useRef, useState, type FormEvent } from 'react';
import { COMMUNITY_PLATFORMS, communityPlatformLabel, type CommunityChannel } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { deleteStaffResourceFile, uploadStaffResourceFile } from '@/lib/bunnyStorage';
import { useToast } from '@/features/ui/toast/useToast';

const emptyForm = {
  platform: 'whatsapp',
  label: '',
  url: '',
  description: '',
  sort_order: 0,
  is_active: true,
  image_url: '',
};

export function AdminCommunitiesPage() {
  const { profile, isAdmin, isStaff } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState<CommunityChannel[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const imageRef = useRef<HTMLInputElement | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data, error: err } = await supabase
      .from('community_channels')
      .select(
        'id, platform, label, url, description, sort_order, is_active, image_url, image_storage_path, created_at, updated_at',
      )
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (err) toast.error(err.message);
    else setRows((data ?? []) as CommunityChannel[]);
  }

  useEffect(() => {
    void load();
  }, []);

  function startEdit(row: CommunityChannel) {
    setEditingId(row.id);
    setForm({
      platform: row.platform,
      label: row.label,
      url: row.url,
      description: row.description ?? '',
      sort_order: row.sort_order,
      is_active: row.is_active,
      image_url: row.image_url ?? '',
    });
    setImageFile(null);
    if (imageRef.current) imageRef.current.value = '';
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
    if (imageRef.current) imageRef.current.value = '';
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isStaff || !profile) return;
    setSaving(true);
    try {
      let image_storage_path: string | null | undefined;
      if (imageFile) {
        const safe = imageFile.name.replace(/[^\w.\-]+/g, '_');
        image_storage_path = `network/communities/${Date.now()}_${safe}`;
        await uploadStaffResourceFile({ storagePath: image_storage_path, file: imageFile });
      }

      const payload: Record<string, unknown> = {
        platform: form.platform,
        label: form.label.trim(),
        url: form.url.trim(),
        description: form.description.trim(),
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
        image_url: form.image_url.trim() || null,
        updated_at: new Date().toISOString(),
      };
      if (image_storage_path) payload.image_storage_path = image_storage_path;

      if (editingId) {
        const { error: err } = await supabase.from('community_channels').update(payload).eq('id', editingId);
        if (err) throw err;
        toast.success('Communauté mise à jour.', 'Communautés');
      } else {
        const { error: err } = await supabase.from('community_channels').insert({
          ...payload,
          created_by: profile.id,
        });
        if (err) throw err;
        toast.success('Communauté créée.', 'Communautés');
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: CommunityChannel) {
    if (!isStaff) return;
    const { error: err } = await supabase
      .from('community_channels')
      .update({ is_active: !row.is_active, updated_at: new Date().toISOString() })
      .eq('id', row.id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function remove(row: CommunityChannel) {
    if (!isAdmin) return;
    if (!window.confirm(`Supprimer « ${row.label} » ?`)) return;
    const { error: err } = await supabase.from('community_channels').delete().eq('id', row.id);
    if (err) toast.error(err.message);
    else {
      if (row.image_storage_path) {
        try {
          await deleteStaffResourceFile({ storagePath: row.image_storage_path });
        } catch {
          /* ignore */
        }
      }
      toast.success('Communauté supprimée.', 'Communautés');
      if (editingId === row.id) resetForm();
      await load();
    }
  }

  return (
    <div>
      <section className="panel" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginTop: 0 }}>Communautés ISBD</h2>
        <p className="muted">
          Créez des communautés avec image de profil et lien d’accès (WhatsApp, Facebook, Telegram…). Visibles
          dans Réseau & connaissances.
        </p>

        {isStaff ? (
          <form onSubmit={(e) => void onSubmit(e)} style={{ marginTop: '1rem' }}>
            <div className="grid-2">
              <div className="field">
                <label>Plateforme</label>
                <select
                  value={form.platform}
                  onChange={(e) => {
                    const platform = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      platform,
                      label: prev.label || `Communauté ${communityPlatformLabel(platform)}`,
                    }));
                  }}
                >
                  {COMMUNITY_PLATFORMS.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Libellé affiché</label>
                <input
                  required
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Ex. Groupe WhatsApp ISBD"
                />
              </div>
            </div>
            <div className="field">
              <label>URL d’invitation / page</label>
              <input
                required
                type="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://…"
              />
            </div>
            <div className="field">
              <label>Description</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Court texte d’invitation"
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Image de profil</label>
                <input ref={imageRef} type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] ?? null)} />
              </div>
              <div className="field">
                <label>Ou URL image</label>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Ordre</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                />
              </div>
              <div className="field">
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '1.6rem' }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  Visible pour les abonnés
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              {editingId ? (
                <button type="button" className="btn btn-ghost" onClick={resetForm}>
                  Annuler
                </button>
              ) : null}
            </div>
          </form>
        ) : null}
      </section>

      <section className="panel">
        <h3 style={{ marginTop: 0 }}>Liste ({rows.length})</h3>
        {rows.length === 0 ? (
          <p className="muted">Aucune communauté. Ajoutez-en ci-dessus après la migration.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {rows.map((row) => (
              <li
                key={row.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  padding: '0.65rem 0',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                <div>
                  <strong>{row.label}</strong>
                  <div className="muted">
                    {communityPlatformLabel(row.platform)}
                    {' · '}
                    {row.is_active ? 'actif' : 'masqué'}
                    {' · ordre '}
                    {row.sort_order}
                  </div>
                  <div className="muted" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {row.url}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <a className="btn btn-ghost" href={row.url} target="_blank" rel="noopener noreferrer">
                    Ouvrir
                  </a>
                  {isStaff ? (
                    <>
                      <button type="button" className="btn btn-ghost" onClick={() => startEdit(row)}>
                        Modifier
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={() => void toggleActive(row)}>
                        {row.is_active ? 'Masquer' : 'Activer'}
                      </button>
                    </>
                  ) : null}
                  {isAdmin ? (
                    <button type="button" className="btn btn-ghost" onClick={() => void remove(row)}>
                      Supprimer
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
